import { useApplicationInformationQuery } from "web_myplann/Api/Miscellaneous/useApplicationInformationQuery";
import { setupClient } from "web_myplann/Infrastructure/Api/Api";
import { setupClient as setupClientOld } from "web_myplann/Infrastructure/ApiOld/Api";
import { useToaster } from "web_myplann/Infrastructure/Hooks/useToaster/useToaster";
import { handleMyPlannResponseError } from "web_myplann/Infrastructure/handleMyPlannResponseError";
import { getRouter } from "web_myplann/Infrastructure/Routing/Router";
import "web_myplann/Infrastructure/sentry.ts";
import { CssBaseline } from "@mui/material";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { MutationCache, QueryCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import i18n from "i18next";
import { useAtomValue, useSetAtom } from "jotai";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { RouterProvider } from "react-router-dom";
import ColorSchemeProvider, {
	useColorScheme,
} from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import { initI18n } from "web_myplann/Infrastructure/Translations";
import UpdateVersionDialog from "web_myplann/Infrastructure/UpdateVersionDialog";
import { MyPlannQueryClient } from "web_myplann/Shared/QueryClient";
import { apiInitializedAtom } from "web_myplann/Shared/State/apiInitializedAtom";
import { appInfoState } from "web_myplann/Shared/State/appInfoAtom";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";
import { getMyPlannTheme } from "web_myplann/Shared/Themes";
import { device } from "web_myplann/Utils/device";
import { handleIncomingMessage } from "web_myplann/Utils/WebViewMessageHandler";
import { z } from "zod";
import { zodI18nMap } from "zod-i18n-map";

// Expose setupClient globally for E2E testing
if (typeof window !== "undefined") {
	(window as any).setupClient = setupClient;
}

const persister = createSyncStoragePersister({
	storage: window.localStorage,
});

const queryClient = new MyPlannQueryClient({
	queryCache: new QueryCache({ onError: handleMyPlannResponseError }),
	mutationCache: new MutationCache({ onError: handleMyPlannResponseError }),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
			gcTime: 2_147_483_647, // ~24.8 days
		},
	},
});

const ReactQueryDevtoolsProduction = React.lazy(() =>
	import("@tanstack/react-query-devtools/build/modern/production.js").then(
		(d) => ({ default: d.ReactQueryDevtools }),
	),
);

const AppInner: React.FunctionComponent = () => {
	const { setMode, mode } = useColorScheme();
	const setApiInitialized = useSetAtom(apiInitializedAtom);
	const setSessionState = useSetAtom(sessionState);
	const setAppInfoState = useSetAtom(appInfoState);
	const { isAuthenticated } = useAtomValue(sessionState);
	const routerRef = useRef(getRouter());

	const { data: applicationInformation } =
		useApplicationInformationQuery(isAuthenticated);

	useEffect(() => {
		async function initializeWeb() {
			const response = await device.initializeWeb();
			const authMiddleware = {
				name: "Add JWT token",
				fn: (request: any) => {
					const headers = new Headers(request.options.headers);
					headers.set("Authorization", `Bearer ${response.payload.bearer}`);
					headers.set("token-verify", response.payload.tokenVerify);
					request.options.headers = headers;
					return request;
				},
			};
			const clientConfig = {
				apiUrl: import.meta.env.VITE_MYPLANN_URL,
				jwtKey: () => `Bearer ${response.payload.bearer}`,
				requestMiddlewares: [authMiddleware],
			};
			setupClient(clientConfig);
			setupClientOld(clientConfig);
			setApiInitialized(true);
			setSessionState({
				isAuthenticated: true,
				token: response.payload.bearer,
				tokenVerify: response.payload.tokenVerify,
			});
			i18n.changeLanguage(response.payload.language);

			setMode({ paletteMode: response.payload.darkMode ? "dark" : "light" });
			setAppInfoState({ appVersion: response.payload.appVersion });
		}
		initializeWeb();
	}, []);

	useEffect(() => {
		if (applicationInformation?.activeColor != null) {
			setMode({ hspColor: applicationInformation.activeColor });
		}
	}, [applicationInformation, setMode]);

	useEffect(() => {
		const messageListener = window.addEventListener(
			"message",
			(nativeEvent) => {
				handleIncomingMessage(
					nativeEvent.data,
					setMode,
					queryClient,
					(path: string) => routerRef.current.navigate(path),
				);
			},
		);
		return messageListener;
	}, [setMode]);

	useEffect(() => {
		const bgColor = mode.paletteMode === "dark" ? "#0F172A" : "#ffffff";
		document.body.style.backgroundColor = bgColor;
		document.documentElement.style.backgroundColor = bgColor;
	}, [mode.paletteMode]);

	return (
		<>
			<RouterProvider router={routerRef.current} />
			<UpdateVersionDialog />
		</>
	);
};

export const App: React.FunctionComponent = () => {
	const [showDevtools, setShowDevtools] = useState(
		import.meta.env.VITE_ALLOW_DEVTOOLS === "true",
	);

	useEffect(() => {
		if (showDevtools) {
			window.toggleDevtools = () => setShowDevtools((old) => !old);
		}
	}, []);

	useToaster();

	initI18n();

	z.setErrorMap(zodI18nMap);

	return (
		<PersistQueryClientProvider
			client={queryClient}
			persistOptions={{ persister }}
			onSuccess={() => {
				if (navigator.onLine) {
					queryClient.resumePausedMutations();
				}
			}}
		>
			<ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
			{showDevtools && (
				<Suspense fallback={null}>
					<ReactQueryDevtoolsProduction
						initialIsOpen={false}
						buttonPosition="bottom-left"
					/>
				</Suspense>
			)}
			<ColorSchemeProvider getTheme={getMyPlannTheme}>
				<CssBaseline enableColorScheme />
				<AppInner />
			</ColorSchemeProvider>
		</PersistQueryClientProvider>
	);
};
